import { ApiErrorMessage, BaseApi } from "api";
import { endpoints } from "config";
import {
  PoiCreatePayload,
  PoiCreateResponseDto,
  PoiCreateResponse,
  PoiDeletePayload,
  PoiDeleteResponse,
} from "./poiModels";
// import { GetCoordinatesResponse } from "./getCoordinatesModels";

class PoiApi extends BaseApi {
  /**
   * Parse errors from `getFarmDetails`
   */
  private static createPoiParseError: typeof this.parseGenericErrorMessageCode =
    (msg) => {
      switch (msg) {
        // API-specific error codes here, generic messages handled below
        case "Invalid_Length":
        case "Invalid_Ath":
        case "Invalid_Atv":
        case "Invalid_Coordinates":
          return "Invalid coordinates";
        case "Scout_Point_Out_Of_Bounds":
          return "Scout point is out of bounds";
        case "Invalid_Field_Data":
          return "Invalid field data";
        default:
          return this.parseGenericErrorMessageCode(msg);
      }
    };

  /**
   * # Add a Point of Interest (POI) to the panorama
   */
  static createPoi = async (payload: PoiCreatePayload) => {
    try {
      // Due to the way the API has been designed, we need to pass the payload
      // as query string parameters instead of the request body...
      // NOTE: `undefined` in types is the placeholder for payload type
      const result = await this.makeRequest<
        PoiCreateResponseDto,
        ApiErrorMessage[],
        undefined
      >({
        url: endpoints.features.createPoi,
        method: "POST",
        config: {
          params: payload,
        },
        // payload,
      });
      if (this.isResponseSuccessful(result)) {
        return this.successResponse<PoiCreateResponse>({
          height_in_meters: result.data.details.height_in_meters,
          // NOTE: `id` from API response is the `id` of the new POI
          id: result.data.id,
        });
      } else {
        return this.errorResponse(
          this.createPoiParseError(result.data.errors[0].message_code)
        );
      }
    } catch (err: unknown) {
      return this.safelyCatchUnknownError<PoiCreateResponse>(
        err,
        "createPoi",
        this.createPoiParseError
      );
    }
  };

  /**
   * Parse errors from `getFarmDetails`
   */
  private static deletePoiParseError: typeof this.parseGenericErrorMessageCode =
    (msg) => {
      switch (msg) {
        // API-specific error codes here, generic messages handled below
        case "Invalid_ScoutPoint_Id":
          return "Invalid scout point selected";
        case "Invalid_Field_Data":
          return "Invalid field data";
        default:
          return this.parseGenericErrorMessageCode(msg);
      }
    };

  static deletePoi = async (payload: PoiDeletePayload) => {
    try {
      // Due to the way the API has been designed, we need to pass the payload
      // as query string parameters instead of the request body...
      // NOTE: `undefined` in types is the placeholder for payload type
      const result = await this.makeRequest<
        PoiDeleteResponse,
        ApiErrorMessage[],
        undefined
      >({
        url: endpoints.features.deletePoi,
        method: "POST",
        config: {
          params: payload,
        },
        // payload,
      });
      if (this.isResponseSuccessful(result)) {
        return this.successResponse(result.data.details);
      } else {
        return this.errorResponse(
          this.deletePoiParseError(result.data.errors[0].message_code)
        );
      }
    } catch (err: unknown) {
      return this.safelyCatchUnknownError<PoiDeleteResponse>(
        err,
        "deletePoi",
        this.deletePoiParseError
      );
    }
  };
}

export default PoiApi;
